import type { Component, ComputedRef, Ref } from 'vue';
import type { NavigationGuard } from 'vue-router';
import type { PricingOption } from '@/interfaces/pricingPage.interface';

export enum BusinessUnitKey {
  Flokee = 'FLOKEE',
  Cleepo = 'CLEEPO',
}

export const DEFAULT_DASHBOARD_ROUTE_NAME = 'DashboardView';
export const DEFAULT_LOGIN_ROUTE_NAME = 'AuthLoginView';
export const DEFAULT_SELECT_ACCOUNT_ROUTE_NAME = 'SelectAccountView';
export const DEFAULT_INVEST_ROUTE_NAME = 'InvestView';

export interface BusinessUnitConfig {
  key: BusinessUnitKey;
  // Position in listings that show every product, e.g. the BU chooser.
  order: number;
  label: string;
  // Route prefix for this BU namespace (e.g. /flokee).
  basePath: string;
  // Production domain that implies this BU.
  domain?: string;
  // Route name to redirect authenticated users to this BU dashboard.
  dashboardRouteName: string;
  // Route name to redirect unauthenticated users to this BU login.
  loginRouteName: string;
  // Route name for account/order switcher view in this BU.
  selectAccountRouteName: string;
  // Route name for the BU invest page (free-plan redirect).
  investRouteName: string;
  // Help center URL for this BU (shown in sidebar support links when available).
  helpCenterUrl?: string;
  // Public product site for this BU, linked from the "About <product>" footer.
  // Branding, so it stays product-owned while support contacts moved to the
  // partner: the footer renders before login, when there is no account and so
  // no partner to read, and it must name the product the visitor is looking at
  // rather than whoever happens to answer its support line (FLOK-1661).
  websiteUrl: string;
  // Privacy policy URL for this BU. Product-owned for want of anywhere else:
  // FLOK-1648 gave the partner no privacy field, so there is nothing to read
  // (FLOK-1661). Flokee's own URL is still outstanding on FLOK-1644.
  privacyPolicyUrl: string;
  supportsTiendanubeSignIn?: boolean;
  // Whether a plan price is shown split into ad investment and service fee.
  showsPlanInvestment?: boolean;
  // i18n key for the BU description on the chooser page.
  chooserDescriptionKey: string;
  /** i18n key prefix for cart plan selection (keys: .title, .description, .disclaimer). */
  cartSelectI18nPrefix: string;
  // Orbit path template for admin deep links.
  orbitPathTemplate: string;
  // Shared UI assets resolved by BU.
  logoPath: string;
  faviconPath: string;
  // localStorage key prefixes that survive a session clear.
  retainedStoragePrefixes?: string[];
}

export interface AccountConfigurationStrategy {
  validateAccount: () => Promise<string | undefined>;
  handleNoCurrentOrder: () => Promise<string | undefined>;
  validateIntegration?: () => Promise<string | undefined>;
}

// What shared code asks of the product it is running for, so it never names one.
// Optional hooks are the ones only some products have.
export interface BusinessUnitRuntime {
  init(): Promise<void>;
  clear(): Promise<void>;
  resolveEntryRoute(): AccountConfigurationStrategy;
  currentEntityId(): number | undefined;
  checkNotices?(): void;
  // Runs before the order is canceled, while what it needs can still be read; the
  // step it returns runs after. Throws to refuse the cancellation.
  onOrderCancel?(order_id: number, type: string): (() => Promise<unknown>) | undefined;
  postAuthenticationRouteName?(default_route_name: string): string;
  globalComponents?: Component[];
  cartGuard?: NavigationGuard;
  cartContext?(): { ecommerce_id?: number; ecommerce_name?: string };
  checkoutSuccessComponent?: Component;
  // Runs between the user refresh and the order summary refresh after a checkout.
  onCheckoutCompleted?(): Promise<unknown>;
  // A composable: runs from setup for every product, before the BU is known, so it must stay
  // idle for another product. Narrows the plans offered outside the cancellation flow.
  usePlanChangeOptions?(options: ComputedRef<PricingOption[]>): {
    options: ComputedRef<PricingOption[]>;
    isLoading: Ref<boolean>;
  };
}
